import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { isActive as isRouteActive, IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';
import { ServicemeshNavigationService } from '@helpers/components/navigation/navigation.service';
import { ServicemeshNavigationItem } from '@helpers/components/navigation/navigation.types';
import { ServicemeshVerticalNavigationBasicItemComponent } from '@helpers/components/navigation/vertical/components/basic/basic.component';
import { ServicemeshVerticalNavigationDividerItemComponent } from '@helpers/components/navigation/vertical/components/divider/divider.component';
import { ServicemeshVerticalNavigationGroupItemComponent } from '@helpers/components/navigation/vertical/components/group/group.component';
import { ServicemeshVerticalNavigationSpacerItemComponent } from '@helpers/components/navigation/vertical/components/spacer/spacer.component';
import { ServicemeshVerticalNavigationComponent } from '@helpers/components/navigation/vertical/vertical.component';
import { filter, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'servicemesh-vertical-navigation-collapsable-item',
  templateUrl: './collapsable.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    MatTooltipModule,
    MatIconModule,
    ServicemeshVerticalNavigationBasicItemComponent,
    forwardRef(() => ServicemeshVerticalNavigationCollapsableItemComponent),
    ServicemeshVerticalNavigationDividerItemComponent,
    forwardRef(() => ServicemeshVerticalNavigationGroupItemComponent),
    ServicemeshVerticalNavigationSpacerItemComponent,
  ],
})
export class ServicemeshVerticalNavigationCollapsableItemComponent implements OnInit, OnDestroy {
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _router = inject(Router);
  private readonly _servicemeshNavigationService = inject(ServicemeshNavigationService);

  /* eslint-disable @typescript-eslint/naming-convention */
  static ngAcceptInputType_autoCollapse: BooleanInput; // NOSONAR
  /* eslint-enable @typescript-eslint/naming-convention */

  @Input() autoCollapse!: boolean;
  @Input() item!: ServicemeshNavigationItem;
  @Input() name!: string;

  isCollapsed: boolean = true;
  isExpanded: boolean = false;
  private _servicemeshVerticalNavigationComponent!: ServicemeshVerticalNavigationComponent;
  private readonly _unsubscribeAll: Subject<unknown> = new Subject<unknown>();

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Host binding for component classes
   */
  @HostBinding('class') get classList(): unknown {
    /* eslint-disable @typescript-eslint/naming-convention */
    return {
      'servicemesh-vertical-navigation-item-collapsed': this.isCollapsed,
      'servicemesh-vertical-navigation-item-expanded': this.isExpanded,
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Get the parent navigation component
    this._servicemeshVerticalNavigationComponent = this._servicemeshNavigationService.getComponent(
      this.name
    );

    // If the item has a children that has a matching url with the current url, expand...
    if (this._hasActiveChild(this.item, this._router.url)) {
      this.expand();
    }
    // Otherwise, if the autoCollapse is on, collapse...
    else if (this.autoCollapse) {
      this.collapse();
    } else {
      // Keep current collapsed/expanded state
    }

    // Listen for the onCollapsableItemCollapsed from the service
    this._servicemeshVerticalNavigationComponent.onCollapsableItemCollapsed
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((collapsedItem) => {
        // Check if the collapsed item is null
        if (collapsedItem === null) {
          return;
        }

        // Collapse if this is a children of the collapsed item
        if (this._isChildrenOf(collapsedItem, this.item)) {
          this.collapse();
        }
      });

    // Listen for the onCollapsableItemExpanded from the service if the autoCollapse is on
    if (this.autoCollapse) {
      this._servicemeshVerticalNavigationComponent.onCollapsableItemExpanded
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((expandedItem) => {
          // Check if the expanded item is null
          if (expandedItem === null) {
            return;
          }

          // Check if this is a parent of the expanded item
          if (this._isChildrenOf(this.item, expandedItem)) {
            return;
          }

          // Check if this has a children with a matching url with the current active url
          if (this._hasActiveChild(this.item, this._router.url)) {
            return;
          }

          // Check if this is the expanded item
          if (this.item === expandedItem) {
            return;
          }

          // If none of the above conditions are matched, collapse this item
          this.collapse();
        });
    }

    // Attach a listener to the NavigationEnd event
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((event: NavigationEnd) => {
        // If the item has a children that has a matching url with the current url, expand...
        if (this._hasActiveChild(this.item, event.urlAfterRedirects)) {
          this.expand();
        }
        // Otherwise, if the autoCollapse is on, collapse...
        else if (this.autoCollapse) {
          this.collapse();
        } else {
          // Keep current collapsed/expanded state
        }
      });

    // Subscribe to onRefreshed on the navigation component
    this._servicemeshVerticalNavigationComponent.onRefreshed
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Collapse
   */
  collapse(): void {
    // Return if the item is disabled
    if (this.item.disabled) {
      return;
    }

    // Return if the item is already collapsed
    if (this.isCollapsed) {
      return;
    }

    // Collapse it
    this.isCollapsed = true;
    this.isExpanded = !this.isCollapsed;

    // Mark for check
    this._changeDetectorRef.markForCheck();

    // Execute the observable
    this._servicemeshVerticalNavigationComponent.onCollapsableItemCollapsed.next(this.item);
  }

  /**
   * Expand
   */
  expand(): void {
    // Return if the item is disabled
    if (this.item.disabled) {
      return;
    }

    // Return if the item is already expanded
    if (!this.isCollapsed) {
      return;
    }

    // Expand it
    this.isCollapsed = false;
    this.isExpanded = !this.isCollapsed;

    // Mark for check
    this._changeDetectorRef.markForCheck();

    // Execute the observable
    this._servicemeshVerticalNavigationComponent.onCollapsableItemExpanded.next(this.item);
  }

  /**
   * Toggle collapsable
   */
  toggleCollapsable(): void {
    // Toggle collapse/expand
    if (this.isCollapsed) {
      this.expand();
    } else {
      this.collapse();
    }
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: Record<string, unknown>): unknown {
    return item.id || index;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Build match options for Router.isActive based on exactMatch flag
   *
   * @param exactMatch
   * @private
   */
  private _buildMatchOptions(exactMatch = false): IsActiveMatchOptions {
    const matchOptions: IsActiveMatchOptions = {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    };
    if (exactMatch) {
      matchOptions.paths = 'exact';
      matchOptions.queryParams = 'exact';
    }
    return matchOptions;
  }

  /**
   * Check if the given item has the given url
   * in one of its children
   *
   * @param item
   * @param currentUrl
   * @private
   */
  private _hasActiveChild(item: ServicemeshNavigationItem, currentUrl: string): boolean {
    const children = item.children;

    if (!children) {
      return false;
    }

    for (const child of children) {
      if (child.children) {
        if (this._hasActiveChild(child, currentUrl)) {
          return true;
        }
      }

      // Check if the child has a link and is active
      if (
        child.link &&
        isRouteActive(child.link, this._router, this._buildMatchOptions(child.exactMatch))()
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if this is a children
   * of the given item
   *
   * @param parent
   * @param item
   * @private
   */
  private _isChildrenOf(
    parent: ServicemeshNavigationItem,
    item: ServicemeshNavigationItem
  ): boolean {
    const children = parent.children;

    if (!children) {
      return false;
    }

    if (children.indexOf(item) > -1) {
      return true;
    }

    for (const child of children) {
      if (child.children) {
        if (this._isChildrenOf(child, item)) {
          return true;
        }
      }
    }

    return false;
  }
}
